/*
 * <rider-clips>
 * - Mouse: a clip plays while hovered; siblings dim (CSS). Touch: clips play while mostly on screen.
 * - Story-style progress bar tracks video playback (transform scaleX, no layout work).
 * - Sound toggle unmutes one clip at a time.
 * - Quick add: posts the tagged product to the cart, morphs + to a check, and updates the header
 *   cart count. Reduced motion: no autoplay.
 */
if (!customElements.get('rider-clips')) {
  class RiderClips extends HTMLElement {
    connectedCallback() {
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      this.clips = Array.from(this.querySelectorAll('.clip'));

      this.clips.forEach((clip) => {
        const video = clip.querySelector('video');
        if (video) this.bindVideo(clip, video);
        const add = clip.querySelector('button.clip__add');
        if (add) add.addEventListener('click', (event) => this.quickAdd(event, add));
      });

      if (!this.finePointer && !this.reduceMotion && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = entry.target.querySelector('video');
            if (!video) return;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.7) video.play().catch(() => {});
            else video.pause();
          });
        }, { threshold: [0, 0.7] });
        this.clips.forEach((clip) => this.observer.observe(clip));
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    bindVideo(clip, video) {
      const fill = clip.querySelector('.clip__progress-fill');
      let frame = null;
      const draw = () => {
        if (video.duration) fill.style.transform = `scaleX(${video.currentTime / video.duration})`;
        frame = video.paused ? null : requestAnimationFrame(draw);
      };
      video.addEventListener('play', () => { clip.classList.add('is-playing'); if (!frame) frame = requestAnimationFrame(draw); });
      video.addEventListener('pause', () => clip.classList.remove('is-playing'));

      if (this.finePointer && !this.reduceMotion) {
        clip.addEventListener('pointerenter', () => video.play().catch(() => {}));
        clip.addEventListener('pointerleave', () => { if (video.muted) video.pause(); });
      }

      const sound = clip.querySelector('.clip__sound');
      if (!sound) return;
      sound.addEventListener('click', () => {
        const turnOn = video.muted;
        this.querySelectorAll('video').forEach((v) => { v.muted = true; });
        this.querySelectorAll('.clip__sound').forEach((b) => {
          b.setAttribute('aria-pressed', 'false');
          b.setAttribute('aria-label', 'Turn sound on');
        });
        if (turnOn) {
          video.muted = false;
          video.play().catch(() => {});
          sound.setAttribute('aria-pressed', 'true');
          sound.setAttribute('aria-label', 'Turn sound off');
        }
      });
    }

    async quickAdd(event, button) {
      event.preventDefault();
      if (button.classList.contains('is-loading')) return;
      button.classList.add('is-loading');
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] }),
        });
        if (!res.ok) throw new Error(res.status);
        button.classList.add('is-added');
        button.setAttribute('aria-label', 'Added to cart');
        this.updateCartCount();
        setTimeout(() => {
          button.classList.remove('is-added');
          button.setAttribute('aria-label', button.getAttribute('aria-label').replace('Added to cart', 'Add to cart'));
        }, 2200);
      } catch (error) {
        const link = button.closest('.clip__product')?.querySelector('.clip__product-main');
        if (link) window.location.href = link.href;
      } finally {
        button.classList.remove('is-loading');
      }
    }

    async updateCartCount() {
      try {
        const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
        const icon = document.getElementById('cart-icon-bubble');
        if (!icon) return;
        let bubble = icon.querySelector('.cart-count-bubble');
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble z-1 font-bold';
          bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
          icon.appendChild(bubble);
        }
        bubble.querySelector('[aria-hidden]').textContent = cart.item_count;
        bubble.querySelector('.visually-hidden').textContent = `${cart.item_count} items in cart`;
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      } catch (e) {}
    }
  }

  customElements.define('rider-clips', RiderClips);
}
